import React from 'react';
import { useTranslation } from 'react-i18next';

import Switch from 'shared/_styles/Switch.style';
import * as Text from 'theme/components/Text.style';
import * as Section from 'settings/styles/SettingsSection.style';
import DeviceNotificationsRow, {
  DeviceNotificationsRowProps,
} from 'settings/components/DeviceNotificationsRow';

export interface NotificationSettings {
  allowShiftNotes: boolean;
  allowConversations: boolean;
}

interface NotificationsProps {
  value: NotificationSettings;
  onChange: (next: NotificationSettings) => void | Promise<void>;
  device: DeviceNotificationsRowProps;
  canAccessShiftNotes?: boolean;
}

export default function Notifications({
  value,
  onChange,
  device,
  canAccessShiftNotes = true,
}: NotificationsProps): JSX.Element {
  const { t } = useTranslation('pageSettings');

  const handleChange = (patch: Partial<NotificationSettings>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <>
      <Section.SectionHeader>
        <Text.H5>{t('notifications.title')}</Text.H5>
      </Section.SectionHeader>

      <Section.GroupLabel $first>
        {t('notifications.onThisDevice')}
      </Section.GroupLabel>
      <Section.SectionCard>
        <Section.Rows>
          <DeviceNotificationsRow {...device} />
        </Section.Rows>
      </Section.SectionCard>

      <Section.GroupLabel>{t('notifications.byEmail')}</Section.GroupLabel>
      <Section.SectionCard>
        <Section.Rows>
          {canAccessShiftNotes && (
            <>
              <Section.RowListItem>
                <Section.RowLabel>
                  <Text.P2Semibold>
                    {t('notifications.email.shiftReminders')}
                  </Text.P2Semibold>
                </Section.RowLabel>
                <Section.RowControl>
                  <Switch
                    checked={value.allowShiftNotes}
                    inputProps={{
                      'aria-label': t('notifications.email.shiftReminders'),
                    }}
                    onChange={(e) =>
                      handleChange({ allowShiftNotes: e.target.checked })
                    }
                  />
                </Section.RowControl>
              </Section.RowListItem>

              <Section.Divider />
            </>
          )}

          <Section.RowListItem>
            <Section.RowLabel>
              <Text.P2Semibold>
                {t('notifications.email.conversationReminders')}
              </Text.P2Semibold>
            </Section.RowLabel>
            <Section.RowControl>
              <Switch
                checked={value.allowConversations}
                inputProps={{
                  'aria-label': t('notifications.email.conversationReminders'),
                }}
                onChange={(e) =>
                  handleChange({ allowConversations: e.target.checked })
                }
              />
            </Section.RowControl>
          </Section.RowListItem>
        </Section.Rows>
      </Section.SectionCard>
    </>
  );
}
